import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Cookie, X } from 'lucide-react';

const STORAGE_KEY = 'dd_cookie_consent_v1';

/**
 * Web-only cookie banner.
 *  - Hidden on Capacitor native apps (no browser cookies used there — JWT is in localStorage).
 *  - Shows once per browser; the user's acknowledgement is stored in localStorage.
 *  - DutchDate uses only strictly-necessary cookies (the HttpOnly refresh token cookie),
 *    so this banner is notice-only — it does NOT gate any tracking.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    } catch {
      // SSR / storage-denied — just keep banner hidden
    }
  }, []);

  if (!visible) return null;
  // Hide during the focused onboarding flows (wizard + AI chat) so the fixed
  // banner never overlaps the bottom action button / consent modal on mobile.
  if (location.pathname === '/onboarding' || location.pathname === '/onboarding-chat') return null;

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch {}
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-[9998] bg-white shadow-2xl rounded-2xl border border-neutral-200 p-4 flex items-start gap-3"
      data-testid="cookie-banner"
      role="dialog"
      aria-live="polite"
    >
      <div className="w-9 h-9 rounded-xl bg-dutch-teal/10 flex items-center justify-center shrink-0">
        <Cookie size={18} className="text-dutch-teal" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-neutral-900 mb-0.5">We use cookies</p>
        <p className="text-[11px] leading-relaxed text-neutral-600">
          DutchDate uses strictly-necessary cookies to keep you signed in. We don't use tracking or
          advertising cookies. See our{' '}
          <a href="/privacy" className="text-dutch-teal hover:underline">Privacy Notice</a>.
        </p>
        <button
          data-testid="cookie-accept"
          onClick={dismiss}
          className="mt-2 px-3 py-1.5 text-[11px] font-semibold rounded-full bg-dutch-teal text-white hover:bg-dutch-teal-dark transition-colors"
        >
          Got it
        </button>
      </div>
      <button
        data-testid="cookie-close"
        onClick={dismiss}
        aria-label="Close"
        className="w-7 h-7 rounded-full hover:bg-neutral-100 flex items-center justify-center text-neutral-400 shrink-0"
      >
        <X size={14} />
      </button>
    </div>
  );
}
